import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  Search,
  Trophy,
  BookOpen,
  Play,
  Loader2,
  Sparkles,
  LogIn,
  LayoutGrid,
  Radio,
  Users,
  UserPlus,
  X,
  Shield,
  FileText,
  ClipboardList,
} from 'lucide-react';

import type {
  PublicRoom,
  PublicQuestion,
  RevealPayload,
  PodiumEntry,
  TeamStanding,
  GameReport,
  AnswerPayload,
  RoomSettings,
  RoomInvite,
} from '../shared/protocol';
import { api, ApiError, setToken, getToken, type Profile, type QuizSummary } from './lib/api';
import { realtime, type ConnectionStatus } from './lib/realtime';
import {
  guestOrNew,
  saveGuest,
  clearGuest,
  readGuest,
  markPendingClaim,
  claimPendingGuestResult,
  type GuestIdentity,
} from './lib/guest';
import { applyTheme } from './lib/theme';
import { BRAND } from './brand';

import { Logo, AppIcon } from './components/Logo';
import { Hero } from './components/Hero';
import { UserMenu } from './components/UserMenu';
import { CreateModeModal } from './components/CreateModeModal';
import type { ModoIa } from './components/QuizCreator';
import { GuardianPanel } from './components/GuardianPanel';
import { AuthModal, type AuthMode } from './components/AuthModal';
import { CompleteProfileModal } from './components/CompleteProfileModal';
import { TermsGateModal } from './components/TermsGateModal';
import { LegalPage, type LegalDoc } from './components/LegalPage';
import { JoinPinModal } from './components/JoinPinModal';
import { QuizCreator } from './components/QuizCreator';
import { LiveRoom } from './components/LiveRoom';
import { Podium, PODIUM_REVEAL_MS } from './components/Podium';
import { RecordingsView } from './components/RecordingsView';
import { SaveResultModal } from './components/SaveResultModal';
import { DahuWallet } from './components/DahuWallet';
import { LeaderboardView } from './components/LeaderboardView';
import { UserProfileView } from './components/UserProfileView';
import { UserSearch } from './components/UserSearch';
import { AnalyticsView } from './components/AnalyticsView';
import { AsyncStudyMode } from './components/AsyncStudyMode';
import { HostSetupModal } from './components/HostSetupModal';
import { OpenRooms, useLobbyRooms } from './components/OpenRooms';
import { CreateMatchModal, type CreateMatchPayload } from './components/CreateMatchModal';
import { FriendsView } from './components/FriendsView';
import { LeaguesView } from './components/LeaguesView';
import { AdminPanel } from './components/admin/AdminPanel';
import { useFriends } from './lib/friends';
import { ThemeToggle } from './components/ThemeToggle';
import { FavoriteButton } from './components/FavoriteButton';
import { ShareButton } from './components/ShareButton';
import { SharedQuizView } from './components/SharedQuizView';
import {
  pathToRoute,
  routeToPath,
  isUnder,
  NEEDS_ACCOUNT,
  SUBPATH_SCREENS,
  type Route,
  type Screen,
  type HomeTab,
} from './lib/routes';
import { notifyLocationChange } from './lib/useSubPath';
import { ExamTake } from './components/ExamTake';
import { TaskTake } from './components/TaskTake';
import { MyContentView, type MineTab } from './components/MyContentView';
import { languageName } from '../shared/languages';

export default function App() {
  /* ---------------- sesión ---------------- */
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);

  /* ---------------- navegación ---------------- */
  // Cada sección tiene su dirección (ver lib/routes): se arranca en la que
  // diga la barra, para que recargar no saque a nadie de donde estaba.
  const [initialRoute] = useState<Route>(() => pathToRoute());
  const [sharedQuizId, setSharedQuizId] = useState<string | null>(
    initialRoute.screen === 'quiz' ? initialRoute.quizId ?? null : null,
  );
  const [legalDoc, setLegalDoc] = useState<LegalDoc>(initialRoute.legalDoc ?? 'terminos');
  const [examCode, setExamCode] = useState<string | null>(initialRoute.examCode ?? null);
  const [taskCode, setTaskCode] = useState<string | null>(initialRoute.taskCode ?? null);
  const [screen, setScreen] = useState<Screen>(initialRoute.screen);
  const [homeTab, setHomeTab] = useState<HomeTab>(initialRoute.homeTab ?? 'quizzes');
  const [studyQuizId, setStudyQuizId] = useState<string | null>(
    initialRoute.screen === 'study' ? initialRoute.quizId ?? null : null,
  );
  const [editingQuizId, setEditingQuizId] = useState<string | null>(
    initialRoute.screen === 'create' ? initialRoute.quizId ?? null : null,
  );
  /** Perfil ajeno abierto, y la pantalla a la que vuelve "Volver". */
  const [viewedUser, setViewedUser] = useState<{ id: string; from: Screen } | null>(
    initialRoute.userId ? { id: initialRoute.userId, from: 'users' } : null,
  );
  const [userQuery, setUserQuery] = useState('');
  /** El editor arranca con el panel de IA desplegado cuando se eligió esa vía. */
  const [crearConIa, setCrearConIa] = useState<ModoIa | false>(false);
  const [eligiendoModo, setEligiendoModo] = useState(false);
  const [abrirAjustesIa, setAbrirAjustesIa] = useState(false);

  /* ---------------- catálogo ---------------- */
  const [quizzes, setQuizzes] = useState<QuizSummary[]>([]);
  const [search, setSearch] = useState('');
  /** Código ISO 639-1, o '' para no filtrar por idioma. */
  const [language, setLanguage] = useState('');
  const [languagesInUse, setLanguagesInUse] = useState<{ code: string; count: number }[]>([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set());
  /** Se incrementa para forzar una recarga del catálogo tras guardar un quiz. */
  const [catalogVersion, setCatalogVersion] = useState(0);

  /* ---------------- partida ---------------- */
  const [myId, setMyId] = useState('');
  const [room, setRoom] = useState<PublicRoom | null>(null);
  const [question, setQuestion] = useState<PublicQuestion | null>(null);
  const [questionEndsAt, setQuestionEndsAt] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [podium, setPodium] = useState<{
    entries: PodiumEntry[];
    prizePool: number;
    teams?: TeamStanding[];
  } | null>(null);
  const [report, setReport] = useState<GameReport | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus>('offline');
  /** Sala de la dirección (/sala/<pin>) a la que se vuelve al recargar, hasta que llega. */
  const [pendingPin, setPendingPin] = useState<string | null>(initialRoute.pin ?? null);
  const pendingPinRef = useRef(pendingPin);
  pendingPinRef.current = pendingPin;

  /* ---------------- modales ---------------- */
  const [showJoin, setShowJoin] = useState(false);
  /** PIN que llega ya puesto desde la sala de espera. */
  const [joinPin, setJoinPin] = useState<string | undefined>();
  const [joinError, setJoinError] = useState('');
  // El manejador de mensajes se registra una vez; lee esto para saber si hay
  // un modal donde mostrar el error o si hay que avisar con un toast.
  const showJoinRef = useRef(false);
  showJoinRef.current = showJoin;
  const [hostQuiz, setHostQuiz] = useState<QuizSummary | null>(null);
  /** «Crear partida» de la sala de espera: una sala que dirige el servidor. */
  const [creatingMatch, setCreatingMatch] = useState(false);
  /** Invitación de un amigo a su sala, a la espera de que se acepte o se descarte. */
  const [invite, setInvite] = useState<RoomInvite | null>(null);
  /** Liga con la que se abre la clasificación al llegar desde la ficha de una liga. */
  const [leaderboardLeague, setLeaderboardLeague] = useState<string | null>(
    initialRoute.leagueId ?? null,
  );
  /** Aviso de "¿guardar tu resultado?" para quien terminó sin cuenta. */
  const [savePrompt, setSavePrompt] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  /** Pestaña abierta en «Mis trivias y mis exámenes». */
  const [mineTab, setMineTab] = useState<MineTab>(initialRoute.mineTab ?? 'quizzes');

  const notify = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 4500);
  }, []);

  const { data: friendsData, reload: reloadFriends } = useFriends(Boolean(profile));

  /* ---------------- dirección ---------------- */
  // La barra de direcciones sigue a la sección abierta: recargar vuelve a
  // ella, el enlace se puede copiar y Atrás/Adelante del navegador funcionan.
  // Cada cambio de sección es una entrada del historial; la primera vez solo
  // se corrige la dirección, sin apilar nada (y sin tocar la ?consulta, que
  // puede traer la vuelta de Google).
  const currentPath = routeToPath({
    screen,
    homeTab,
    quizId:
      screen === 'quiz'
        ? sharedQuizId ?? undefined
        : screen === 'study'
          ? studyQuizId ?? undefined
          : screen === 'create'
            ? editingQuizId ?? undefined
            : undefined,
    examCode: examCode ?? undefined,
    taskCode: taskCode ?? undefined,
    legalDoc,
    userId: viewedUser?.id,
    mineTab,
    leagueId: leaderboardLeague ?? undefined,
    pin: room?.pin ?? pendingPin ?? undefined,
  });
  const replaceNextPath = useRef(true);
  useEffect(() => {
    const replace = replaceNextPath.current;
    replaceNextPath.current = false;
    if (location.pathname === currentPath) return;
    // Lo que cuelga de la base (/ligas/<id>) es de la propia sección.
    if (SUBPATH_SCREENS.has(screen) && isUnder(location.pathname, currentPath)) return;
    if (replace) window.history.replaceState({}, '', currentPath + location.search);
    else window.history.pushState({}, '', currentPath);
    notifyLocationChange();
  }, [currentPath]);

  // Atrás/Adelante: se abre la sección que diga la nueva dirección. El
  // manejador se registra una vez y llama siempre a la última versión.
  const popRef = useRef<() => void>(() => {});
  popRef.current = () => {
    const r = pathToRoute();
    const enSala = room;
    if (enSala && !(r.screen === 'room' && r.pin === enSala.pin)) {
      // Salir de una partida con Atrás sin querer se pagaría caro.
      if (!window.confirm('¿Salir de la partida?')) {
        window.history.pushState({}, '', currentPath);
        return;
      }
      realtime.leaveRoom();
      resetGame();
    }
    // Si solo cambió la vista interna de la sección, App no tiene nada que
    // corregir, y marcar la siguiente como sustitución se comería una entrada.
    if (routeToPath(r) !== currentPath) replaceNextPath.current = true;
    applyRoute(r);
  };
  useEffect(() => {
    const onPop = () => popRef.current();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /* ================================================================
   * Sesión
   * ================================================================ */

  useEffect(() => {
    void (async () => {
      // Vuelta de Google: la URL trae un vale de un solo uso, no el token.
      // Se canjea y se limpia la barra de direcciones antes de nada, para que
      // no quede en el historial ni se reenvíe al recargar.
      const params = new URLSearchParams(location.search);
      const oauthCode = params.get('oauth');

      if (oauthCode) {
        window.history.replaceState({}, '', location.pathname);
        try {
          const data = await api.post<{ token: string; profile: Profile }>(
            '/auth/google/exchange',
            { code: oauthCode },
          );
          setToken(data.token);
          // Si se fue a Google desde el aviso de guardar el resultado, la
          // partida de invitado se pasa ahora a la cuenta.
          const conPartida = await claimPendingGuestResult();
          setProfile(conPartida ?? data.profile);
          notify(
            conPartida
              ? 'Tu resultado quedó guardado en tu cuenta.'
              : `Hola, ${data.profile.displayName}.`,
          );
        } catch (err) {
          notify(err instanceof Error ? err.message : 'No se pudo completar el inicio con Google.');
        } finally {
          setLoadingSession(false);
        }
        return;
      }

      if (!getToken()) {
        setLoadingSession(false);
        return;
      }
      try {
        const data = await api.get<{ profile: Profile }>('/auth/me');
        setProfile(data.profile);
        // El tema guardado en la cuenta gana al del navegador: es el que sigue
        // a la persona entre dispositivos.
        if (data.profile.theme) applyTheme(data.profile.theme);
      } catch {
        setToken(null);
      } finally {
        setLoadingSession(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================================================================
   * Catálogo
   * ================================================================ */

  useEffect(() => {
    const handle = setTimeout(() => {
      void (async () => {
        setLoadingQuizzes(true);
        try {
          const params = new URLSearchParams();
          if (search.trim()) params.set('q', search.trim());
          if (language) params.set('lang', language);
          const cola = params.toString();
          const data = await api.get<{
            quizzes: QuizSummary[];
            languages: { code: string; count: number }[];
          }>(cola ? `/quizzes?${cola}` : '/quizzes');
          setQuizzes(data.quizzes);
          setLanguagesInUse(data.languages ?? []);
        } catch {
          notify('No se pudo cargar el catálogo.');
        } finally {
          setLoadingQuizzes(false);
        }
      })();
    }, search ? 280 : 0); // sin retardo en la primera carga

    return () => clearTimeout(handle);
  }, [search, language, catalogVersion, notify]);

  /* ================================================================
   * Tiempo real
   * ================================================================ */

  useEffect(() => {
    const offStatus = realtime.onStatus(setConnection);

    const off = realtime.on((msg) => {
      switch (msg.t) {
        case 'hello':
          setMyId(msg.userId);
          // Teníamos sesión y el servidor nos trata como invitado: la cerraron
          // (bloqueo, contraseña reseteada, sesiones cerradas por un admin).
          if (msg.isGuest && getToken()) {
            api.get('/auth/me').catch((err) => {
              if (err instanceof ApiError && err.status === 401) {
                setToken(null);
                setProfile(null);
                notify('Tu sesión se cerró. Vuelve a entrar.');
              }
            });
          }
          if (!msg.isGuest) {
            setProfile((prev) => (prev ? { ...prev, dahus: msg.dahus } : prev));
          }
          // El servidor pone un apodo genérico si el pedido no vale. Antes lo
          // hacía en silencio y aparecías como "Invitado 1234" sin saber por qué.
          if (msg.nicknameRejected) {
            notify(`${msg.nicknameRejected} Entras como ${msg.displayName}.`);
          }
          break;

        case 'room:state':
          setRoom(msg.room);
          setPendingPin(null);
          setScreen('room');
          if (msg.room.status === 'lobby') {
            setQuestion(null);
            setReveal(null);
            setPodium(null);
            setReport(null);
          }
          if (msg.room.status === 'question') setReveal(null);
          break;

        case 'room:countdown':
          setCountdown(msg.secondsLeft);
          break;

        case 'room:question':
          setQuestion(msg.question);
          setQuestionEndsAt(msg.endsAt);
          setReveal(null);
          setCountdown(null);
          break;

        // Al contestar alguien, el servidor manda este aviso ligero en vez de
        // reenviar la sala entera. Hay que aplicarlo a mano: si no, el
        // contador de "cuántos faltan" se queda congelado en cero.
        case 'room:answered':
          setRoom((prev) =>
            prev
              ? {
                  ...prev,
                  players: prev.players.map((p) =>
                    p.id === msg.playerId ? { ...p, hasAnswered: true } : p,
                  ),
                }
              : prev,
          );
          break;

        case 'room:reveal':
          setReveal(msg.reveal);
          break;

        case 'room:podium':
          setPodium({ entries: msg.podium, prizePool: msg.prizePool, teams: msg.teams });
          // El saldo cambia si ganaste parte del bote.
          void refreshProfile();
          break;

        case 'room:report':
          setReport(msg.report);
          break;

        case 'room:closed':
          notify(msg.reason);
          resetGame();
          break;

        case 'kicked':
          notify('El anfitrión te sacó de la sala.');
          resetGame();
          break;

        case 'invite':
          setInvite(msg.invite);
          break;

        case 'error':
          // La sala de la dirección ya no existe o no admite volver: al inicio.
          if (
            pendingPinRef.current &&
            ['ROOM_NOT_FOUND', 'ROOM_FULL', 'ROOM_STARTED', 'ROOM_FINISHED'].includes(msg.code)
          ) {
            setPendingPin(null);
            replaceNextPath.current = true;
            setScreen((s) => (s === 'room' ? 'home' : s));
            notify(msg.message);
            break;
          }
          if (
            showJoinRef.current &&
            (msg.code === 'ROOM_NOT_FOUND' || msg.code === 'ROOM_FULL' || msg.code === 'ROOM_STARTED')
          ) {
            setJoinError(msg.message);
          } else {
            notify(msg.message);
          }
          break;
      }
    });

    return () => {
      off();
      offStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notify]);

  // Conecta (o reconecta con el nuevo token) cuando cambia la identidad.
  useEffect(() => {
    if (loadingSession) return;
    realtime.disconnect();
    if (profile) {
      // Con cuenta manda el token: el servidor usa el nombre y el avatar
      // guardados e ignora lo que anuncie el navegador.
      realtime.connect();
      // La identidad de invitado ya no pinta nada, y dejarla puesta haría que
      // al cerrar sesión reapareciera el apodo de antes de registrarse.
      clearGuest();
    } else {
      const guest = guestOrNew();
      realtime.connect({ name: guest.name, avatar: guest.avatar });
    }
    // Se recargó dentro de una partida: se vuelve a ella. El servidor
    // reconoce a quien ya estaba y le devuelve su sitio.
    if (pendingPinRef.current) realtime.send({ t: 'room:join', pin: pendingPinRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSession, profile?.id]);

  // Favoritas de la cuenta, para pintar los corazones del catálogo.
  useEffect(() => {
    if (!profile?.id) {
      setFavoriteIds(new Set());
      return;
    }
    let vigente = true;
    api
      .get<{ ids: string[] }>('/quizzes/favorites/ids')
      .then((data) => vigente && setFavoriteIds(new Set(data.ids)))
      .catch(() => {
        /* sin corazones hasta la próxima carga */
      });
    return () => {
      vigente = false;
    };
  }, [profile?.id]);

  const refreshProfile = useCallback(async () => {
    if (!getToken()) return;
    try {
      const data = await api.get<{ profile: Profile }>('/auth/me');
      setProfile(data.profile);
    } catch {
      /* el perfil se refresca en la siguiente ocasión */
    }
  }, []);

  const resetGame = useCallback(() => {
    setRoom(null);
    setQuestion(null);
    setReveal(null);
    setPodium(null);
    setReport(null);
    setCountdown(null);
    setSavePrompt(false);
    setPendingPin(null);
    // La dirección de la sala se sustituye: con Atrás no se vuelve a una
    // partida de la que ya se salió.
    replaceNextPath.current = true;
    setScreen('home');
  }, []);

  /** Abre la sección de una dirección (al ir Atrás/Adelante). */
  const applyRoute = (r: Route) => {
    if (r.homeTab) setHomeTab(r.homeTab);
    if (r.legalDoc) setLegalDoc(r.legalDoc);
    if (r.mineTab) setMineTab(r.mineTab);
    if (r.screen === 'quiz') setSharedQuizId(r.quizId ?? null);
    if (r.screen === 'study') setStudyQuizId(r.quizId ?? null);
    if (r.screen === 'create') {
      setEditingQuizId(r.quizId ?? null);
      setCrearConIa(false);
    }
    if (r.screen === 'exam') setExamCode(r.examCode ?? null);
    if (r.screen === 'task') setTaskCode(r.taskCode ?? null);
    if (r.screen === 'user' && r.userId) {
      setViewedUser((prev) => ({ id: r.userId!, from: prev?.from ?? 'users' }));
    }
    if (r.screen === 'leaderboard') setLeaderboardLeague(r.leagueId ?? null);
    if (r.screen === 'room' && r.pin && r.pin !== room?.pin) {
      setPendingPin(r.pin);
      realtime.send({ t: 'room:join', pin: r.pin });
    }
    setScreen(r.screen);
    window.scrollTo(0, 0);
  };

  // Una sección de cuenta abierta sin sesión (recargar tras caducar, un enlace
  // ajeno) no tiene nada que enseñar: se vuelve al inicio.
  useEffect(() => {
    if (loadingSession || profile || !NEEDS_ACCOUNT.has(screen)) return;
    replaceNextPath.current = true;
    setScreen('home');
  }, [loadingSession, profile, screen]);

  // Quien termina una partida sin cuenta ve primero el podio y, un momento
  // después, el ofrecimiento de guardar su resultado. Solo si de verdad jugó
  // (está en el podio) y el servidor le dio credenciales con las que reclamarlo.
  useEffect(() => {
    if (!podium || profile) return;
    const guest = readGuest();
    if (!guest?.token || !podium.entries.some((p) => p.id === myId)) return;
    // Después de la revelación del podio, para no taparla.
    const t = setTimeout(() => setSavePrompt(true), PODIUM_REVEAL_MS + 1500);
    return () => clearTimeout(t);
  }, [podium, profile, myId]);

  const handleAuthenticated = async (p: Profile) => {
    // Antes de guardar el perfil: al cambiar de identidad se borran las
    // credenciales de invitado que hacen falta para reclamar la partida.
    const conPartida = await claimPendingGuestResult();
    setProfile(conPartida ?? p);
    setAuthMode(null);
    setSavePrompt(false);
    if (conPartida) notify('Tu resultado quedó guardado en tu cuenta.');
  };

  /* ================================================================
   * Acciones
   * ================================================================ */

  const handleHost = (quiz: QuizSummary) => {
    if (!profile) {
      setAuthMode('register');
      return;
    }
    setHostQuiz(quiz);
  };

  // El perfil propio tiene su pantalla, con edición y datos privados: si
  // alguien se pulsa a sí mismo, va a esa y no a la vista pública.
  const openUser = (id: string, from: Screen) => {
    if (profile && id === profile.id) {
      setScreen('profile');
      return;
    }
    setViewedUser({ id, from });
    setScreen('user');
    window.scrollTo(0, 0);
  };

  const toggleFavorite = async (quizId: string) => {
    if (!profile) {
      notify('Entra en tu cuenta para guardar trivias favoritas.');
      setAuthMode('login');
      return;
    }
    const on = !favoriteIds.has(quizId);
    const aplicar = (marcar: boolean) =>
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (marcar) next.add(quizId);
        else next.delete(quizId);
        return next;
      });
    // Se pinta al momento y se deshace si el servidor lo rechaza: esperar la
    // respuesta para rellenar un corazón se nota como un botón que no va.
    aplicar(on);
    try {
      if (on) await api.put(`/quizzes/${quizId}/favorite`);
      else await api.delete(`/quizzes/${quizId}/favorite`);
    } catch (err) {
      aplicar(!on);
      notify(err instanceof Error ? err.message : 'No se pudo guardar la favorita.');
    }
  };

  const startHosting = (settings: Partial<RoomSettings>, startAt: number | null, inviteIds: string[]) => {
    if (!hostQuiz) return;
    realtime.send({ t: 'room:create', quizId: hostQuiz.id, settings, startAt, inviteIds });
    setHostQuiz(null);
  };

  const openCreateMatch = () => {
    if (!profile) {
      notify('Crea una cuenta gratis para abrir partidas.');
      setAuthMode('register');
      return;
    }
    setCreatingMatch(true);
  };

  // La ventana sigue abierta hasta que llega la sala: si el servidor rechaza
  // algo (la IA sin clave, una trivia que ya no existe), lo elegido no se pierde.
  const createMatch = (payload: CreateMatchPayload) => {
    realtime.send({ t: 'room:createAuto', ...payload });
  };

  const acceptInvite = (inv: RoomInvite) => {
    setInvite(null);
    joinFromLobby(inv.pin);
  };

  const handleCreateQuiz = () => {
    // Sin cuenta no se puede publicar nada, asi que el boton pide entrar en vez
    // de abrir un editor cuyo guardado fallaria al final.
    if (!profile) return setAuthMode('register');
    setEligiendoModo(true);
  };

  const openMine = (tab: MineTab) => {
    setMineTab(tab);
    setScreen('mine');
  };
  /** Cada «Crear examen» de la barra sube este número y abre el formulario. */
  const [examCreateRequest, setExamCreateRequest] = useState(0);
  const handleCreateExam = () => {
    if (!profile) return setAuthMode('register');
    setMineTab('exams');
    setScreen('mine');
    setExamCreateRequest((n) => n + 1);
  };
  /** Lo mismo con «Crear tarea». */
  const [taskCreateRequest, setTaskCreateRequest] = useState(0);
  const handleCreateTask = () => {
    if (!profile) return setAuthMode('register');
    setMineTab('tasks');
    setScreen('mine');
    setTaskCreateRequest((n) => n + 1);
  };

  const abrirEditor = (conIa: ModoIa | false) => {
    setEligiendoModo(false);
    setCrearConIa(conIa);
    setEditingQuizId(null);
    setScreen('create');
  };

  const handleJoin = (pin: string, guest?: GuestIdentity) => {
    setJoinError('');
    // El apodo y el avatar se eligen en el mismo modal que el PIN, es decir
    // DESPUÉS de haber conectado. Se anuncian por el socket ya abierto.
    //
    // Antes esto cerraba la conexión y abría otra, y como el servidor creaba
    // una identidad de invitado nueva en cada `auth`, una sola persona entraba
    // a la sala como dos jugadores.
    if (guest) {
      saveGuest(guest);
      realtime.send({ t: 'guest:identify', guestName: guest.name, guestAvatar: guest.avatar });
    }
    realtime.send({ t: 'room:join', pin });
  };

  // Desde la sala de espera: con cuenta se entra directo; sin ella se pasa por
  // el modal del PIN, ya relleno, para elegir apodo y avatar.
  const joinFromLobby = (pin: string) => {
    if (profile) {
      handleJoin(pin);
      return;
    }
    setJoinError('');
    setJoinPin(pin);
    setShowJoin(true);
  };

  // Cerrar el modal cuando la sala llega de verdad.
  useEffect(() => {
    if (room) {
      setShowJoin(false);
      setCreatingMatch(false);
    }
  }, [room]);

  // La invitación caduca sola: pasado un rato la partida pudo empezar o cerrarse.
  useEffect(() => {
    if (!invite) return;
    const id = window.setTimeout(() => setInvite(null), 60_000);
    return () => window.clearTimeout(id);
  }, [invite]);

  const handleLogout = async (message?: string) => {
    try {
      if (getToken()) await api.post('/auth/logout');
    } catch {
      /* la sesión local se limpia igual (o la cuenta ya no existe) */
    }
    setToken(null);
    setProfile(null);
    resetGame();
    if (message) notify(message);
  };

  const openLegal = (doc: LegalDoc) => {
    setLegalDoc(doc);
    setScreen('legal');
  };

  const answer = (payload: AnswerPayload) => {
    if (!question) return;
    realtime.send({ t: 'answer:submit', questionIndex: question.index, payload });
  };

  /* ================================================================
   * Render
   * ================================================================ */

  if (loadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <AppIcon size={56} className="animate-pulse" />
      </div>
    );
  }

  // La partida ocupa la pantalla entera: nada de cabecera ni navegación.
  const authModal = authMode && (
    <AuthModal
      initialMode={authMode}
      onClose={() => {
        setAuthMode(null);
        // Cerrar el registro sin terminarlo es decir que no.
        markPendingClaim(false);
      }}
      onAuthenticated={(p) => void handleAuthenticated(p)}
    />
  );

  const toastEl = toast && (
    <div
      role="status"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-rise surface-raised px-5 py-3 text-ink-100 max-w-md text-center"
    >
      {toast}
    </div>
  );

  const inviteEl = invite && room?.pin !== invite.pin && (
    <div
      role="alertdialog"
      aria-labelledby="invite-title"
      className="fixed top-20 right-4 left-4 sm:left-auto sm:w-96 z-50 animate-rise surface-raised bg-ink-900 p-4 shadow-2xl"
    >
      <div className="flex items-start gap-3">
        <span className="font-emoji text-3xl leading-none" aria-hidden="true">
          {invite.coverEmoji}
        </span>
        <div className="min-w-0 flex-1">
          <p id="invite-title" className="text-sm text-ink-300">
            <span className="font-semibold text-ink-50">{invite.fromName}</span> te invita a jugar
          </p>
          <p className="font-semibold text-ink-100 truncate">{invite.quizTitle}</p>
        </div>
        <button
          onClick={() => setInvite(null)}
          className="p-1.5 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800"
          aria-label="Descartar invitación"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => setInvite(null)}
          className="flex-1 h-10 rounded-lg text-sm font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-200"
        >
          Ahora no
        </button>
        <button
          onClick={() => acceptInvite(invite)}
          className="flex-[2] h-10 rounded-lg text-sm font-bold bg-ember-500 hover:bg-ember-400 text-on-accent"
        >
          {room ? 'Ir a esa partida' : 'Unirme'}
        </button>
      </div>
    </div>
  );

  if (screen === 'room' && podium) {
    const myIndex = podium.entries.findIndex((p) => p.id === myId);
    const offerSave = (mode: AuthMode) => {
      markPendingClaim(true);
      setSavePrompt(false);
      setAuthMode(mode);
    };
    return (
      <>
        <Podium
          podium={podium.entries}
          teams={podium.teams}
          prizePool={podium.prizePool}
          myId={myId}
          report={report}
          isHost={room?.hostId === myId}
          onHome={() => {
            realtime.leaveRoom();
            resetGame();
          }}
        />
        {savePrompt && !profile && myIndex >= 0 && (
          <SaveResultModal
            entry={podium.entries[myIndex]}
            placement={myIndex + 1}
            totalPlayers={podium.entries.length}
            onRegister={() => offerSave('register')}
            onLogin={() => offerSave('login')}
            onClose={() => setSavePrompt(false)}
          />
        )}
        {authModal}
        {inviteEl}
        {toastEl}
      </>
    );
  }

  if (screen === 'room' && room) {
    return (
      <>
      <LiveRoom
        room={room}
        question={question}
        questionEndsAt={questionEndsAt}
        countdown={countdown}
        reveal={reveal}
        myId={myId}
        connection={connection}
        serverNow={() => realtime.serverNow()}
        onStart={() => realtime.send({ t: 'host:start' })}
        onNext={() => realtime.send({ t: 'host:next' })}
        onSkip={() => realtime.send({ t: 'host:skip' })}
        onKick={(id) => realtime.send({ t: 'host:kick', playerId: id })}
        onSetTeam={(team) => realtime.send({ t: 'room:setTeam', team })}
        onAnswer={answer}
        onLeave={() => {
          realtime.leaveRoom();
          resetGame();
        }}
        onReady={(ready) => realtime.send({ t: 'room:ready', ready })}
        onSchedule={(startAt) => realtime.send({ t: 'host:schedule', startAt })}
        friends={friendsData?.friends ?? null}
        canInvite={Boolean(profile)}
        onManageFriends={() => {
          notify('Sal de la sala para gestionar tus amigos; aquí puedes invitar a los que ya tienes.');
        }}
      />
      {inviteEl}
      {toastEl}
      </>
    );
  }

  // El examen ocupa la pantalla entera, sin la barra: solo el reloj y las
  // preguntas, y nada a mano que invite a salir de la página.
  if (screen === 'exam') {
    return (
      <div className="min-h-screen">
        <ExamTake
          key={examCode ?? 'sin-codigo'}
          initialCode={examCode}
          defaultName={
            profile?.firstName && profile.lastName
              ? { firstName: profile.firstName, lastName: profile.lastName }
              : null
          }
          onExit={() => {
            setExamCode(null);
            setScreen('home');
          }}
        />
        {toastEl}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        profile={profile}
        connection={connection}
        screen={screen}
        onNavigate={(s) => {
          // «Ranking» desde la barra abre la clasificación global, no la de la
          // última liga que se miró.
          if (s === 'leaderboard') setLeaderboardLeague(null);
          setScreen(s);
        }}
        friendRequests={friendsData?.incoming.length ?? 0}
        onCreateQuiz={handleCreateQuiz}
        onCreateExam={handleCreateExam}
        onCreateTask={handleCreateTask}
        onOpenMine={openMine}
        onLogin={() => setAuthMode('login')}
        onRegister={() => setAuthMode('register')}
        onSignOut={() => void handleLogout()}
      />

      <main className="flex-1">
        {screen === 'room' && !room && (
          <div className="flex justify-center py-24 text-ink-400">
            <Loader2 className="w-8 h-8 animate-spin" aria-label="Volviendo a la partida" />
          </div>
        )}

        {screen === 'home' && (
          <HomeScreen
            quizzes={quizzes}
            loading={loadingQuizzes}
            search={search}
            onSearch={setSearch}
            tab={homeTab}
            onTab={setHomeTab}
            language={language}
            onLanguage={setLanguage}
            languagesInUse={languagesInUse}
            profile={profile}
            onHost={handleHost}
            onStudy={(id) => {
              setStudyQuizId(id);
              setScreen('study');
            }}
            onJoin={() => {
              setJoinPin(undefined);
              setShowJoin(true);
            }}
            onJoinRoom={joinFromLobby}
            onCreateMatch={openCreateMatch}
            onCreate={handleCreateQuiz}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            onOpenQuiz={(id) => {
              setSharedQuizId(id);
              setScreen('quiz');
              window.scrollTo(0, 0);
            }}
          />
        )}

        {screen === 'quiz' && sharedQuizId && (
          <SharedQuizView
            quizId={sharedQuizId}
            onHost={handleHost}
            onStudy={(id) => {
              setStudyQuizId(id);
              setScreen('study');
            }}
            onOpenUser={(id) => openUser(id, 'quiz')}
            onBack={() => setScreen('home')}
          />
        )}

        {screen === 'create' && (
          <QuizCreator
            quizId={editingQuizId}
            iniciarConIa={crearConIa}
            onSaved={(quiz) => {
              notify(`"${quiz.title}" guardado.`);
              setScreen('home');
              setCatalogVersion((v) => v + 1);
            }}
            onCancel={() => setScreen('home')}
          />
        )}

        {screen === 'study' && studyQuizId && (
          <AsyncStudyMode
            quizId={studyQuizId}
            // Si se llegó desde la ficha de la trivia, se vuelve a ella.
            onExit={() => setScreen(studyQuizId === sharedQuizId ? 'quiz' : 'home')}
          />
        )}

        {screen === 'leaderboard' && (
          <LeaderboardView
            // Al llegar desde una liga se vuelve a montar con ella elegida.
            key={leaderboardLeague ?? 'global'}
            myId={profile?.id ?? ''}
            myCountry={profile?.country}
            initialLeague={leaderboardLeague}
            onOpenUser={(id) => openUser(id, 'leaderboard')}
            onOpenLeagues={() => setScreen('leagues')}
          />
        )}

        {(screen === 'users' || screen === 'leagues') && (
          <PlayersTabs active={screen} onChange={setScreen} />
        )}

        {screen === 'leagues' && (
          <LeaguesView
            myId={profile?.id ?? null}
            onOpenUser={(id) => openUser(id, 'leagues')}
            onLogin={() => setAuthMode('login')}
            onLeaderboard={(id) => {
              setLeaderboardLeague(id);
              setScreen('leaderboard');
            }}
            notify={notify}
          />
        )}

        {screen === 'users' && (
          <UserSearch
            query={userQuery}
            onQueryChange={setUserQuery}
            onOpenUser={(id) => openUser(id, 'users')}
          />
        )}

        {screen === 'user' && viewedUser && (
          <UserProfileView
            userId={viewedUser.id}
            onBack={() => setScreen(viewedUser.from)}
            canFriend={Boolean(profile)}
            onFriendsChanged={() => void reloadFriends()}
            notify={notify}
          />
        )}

        {screen === 'friends' && profile && (
          <FriendsView
            myId={profile.id}
            data={friendsData}
            reload={reloadFriends}
            onOpenUser={(id) => openUser(id, 'friends')}
            notify={notify}
          />
        )}

        {screen === 'profile' && profile && (
          <AnalyticsView
            profile={profile}
            onProfileChange={setProfile}
            abrirEnIa={abrirAjustesIa}
            onAbierto={() => setAbrirAjustesIa(false)}
            onHostQuiz={handleHost}
            onOpenMine={() => openMine('quizzes')}
            onStudyQuiz={(id) => {
              setStudyQuizId(id);
              setScreen('study');
            }}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            onAccountDeleted={() => void handleLogout('Tu cuenta y sus datos se han borrado.')}
          />
        )}

        {screen === 'legal' && (
          <LegalPage doc={legalDoc} onNavigate={setLegalDoc} onBack={() => setScreen('home')} />
        )}

        {screen === 'mine' && profile && (
          <MyContentView
            profile={profile}
            tab={mineTab}
            onTab={setMineTab}
            onHostQuiz={handleHost}
            onEditQuiz={(id) => {
              setEditingQuizId(id);
              setCrearConIa(false);
              setScreen('create');
            }}
            onCreateQuiz={handleCreateQuiz}
            onCatalogChange={() => setCatalogVersion((v) => v + 1)}
            examCreateRequest={examCreateRequest}
            taskCreateRequest={taskCreateRequest}
          />
        )}

        {screen === 'task' && (
          <TaskTake
            key={taskCode ?? 'sin-codigo'}
            initialCode={taskCode}
            defaultName={
              profile?.firstName && profile.lastName
                ? { firstName: profile.firstName, lastName: profile.lastName }
                : null
            }
            onExit={() => {
              setTaskCode(null);
              setScreen('home');
            }}
          />
        )}

        {screen === 'recordings' && profile?.canMonitor && (
          <RecordingsView onBack={() => setScreen('home')} />
        )}

        {screen === 'guardian' && profile?.role === 'mega_guardian' && <GuardianPanel />}

        {screen === 'admin' && profile?.isAdmin && (
          <AdminPanel myId={profile.id} onOpenUser={(id) => openUser(id, 'admin')} notify={notify} />
        )}

        {screen === 'wallet' && profile && (
          <DahuWallet
            restrictedMinor={profile.restrictedMinor}
            balance={profile.dahus}
            onBalanceChange={(dahus) => setProfile((p) => (p ? { ...p, dahus } : p))}
          />
        )}
      </main>

      <Footer onLegal={openLegal} />

      {/* Modales */}

      {eligiendoModo && profile && (
        <CreateModeModal
          profile={profile}
          onManual={() => abrirEditor(false)}
          onConIa={(modo) => abrirEditor(modo)}
          onConfigurarIa={() => {
            setEligiendoModo(false);
            setScreen('profile');
            // El perfil abre su editor en la pestaña de IA; ver AnalyticsView.
            setAbrirAjustesIa(true);
          }}
          onClose={() => setEligiendoModo(false)}
        />
      )}

      {/* Una cuenta a la que le falta fecha de nacimiento o pais no puede usar
          el sitio: llega asi desde Google, que no entrega ninguno de los dos.
          Va antes que el resto y sin forma de cerrarse, o el requisito seria
          decorativo. */}
      {profile && !profile.profileComplete && screen !== 'legal' && (
        <CompleteProfileModal
          profile={profile}
          onCompleted={(p) => {
            setProfile(p);
            notify('Cuenta lista. A jugar.');
          }}
          onSignedOut={(message) => void handleLogout(message)}
        />
      )}

      {/* Después del perfil: una cuenta anterior a los términos (o a un cambio
          de fondo) los acepta antes de seguir, y una que ya no tiene edad
          para su país se entera aquí y puede llevarse o borrar sus datos. La
          página legal queda accesible para poder leerlos. */}
      {profile && profile.profileComplete && (!profile.termsAccepted || !profile.ageAllowed) &&
        screen !== 'legal' && (
        <TermsGateModal
          profile={profile}
          onAccepted={setProfile}
          onSignedOut={(message) => void handleLogout(message)}
        />
      )}

      {authModal}

      {showJoin && (
        <JoinPinModal
          error={joinError}
          asGuest={!profile}
          initialPin={joinPin}
          onJoin={handleJoin}
          onExam={(pin) => {
            setShowJoin(false);
            setJoinError('');
            setExamCode(pin);
            setScreen('exam');
          }}
          onClose={() => {
            setShowJoin(false);
            setJoinError('');
          }}
        />
      )}

      {hostQuiz && (
        <HostSetupModal
          quiz={hostQuiz}
          balance={profile?.dahus ?? 0}
          canMonitor={profile?.canMonitor}
          onStart={startHosting}
          onClose={() => setHostQuiz(null)}
          friends={friendsData?.friends ?? null}
          myId={profile?.id ?? ''}
          onManageFriends={() => {
            setHostQuiz(null);
            setScreen('friends');
          }}
        />
      )}

      {creatingMatch && profile && (
        <CreateMatchModal
          profile={profile}
          friends={friendsData?.friends ?? null}
          onCreate={createMatch}
          onConfigureAi={() => {
            setCreatingMatch(false);
            setScreen('profile');
            setAbrirAjustesIa(true);
          }}
          onManageFriends={() => {
            setCreatingMatch(false);
            setScreen('friends');
          }}
          onClose={() => setCreatingMatch(false)}
        />
      )}

      {inviteEl}
      {toastEl}
    </div>
  );
}

/* ================================================================
 * Cabecera
 * ================================================================ */

const Header: React.FC<{
  profile: Profile | null;
  connection: ConnectionStatus;
  screen: Screen;
  onNavigate: (s: Screen) => void;
  friendRequests: number;
  onCreateQuiz: () => void;
  onCreateExam: () => void;
  onCreateTask: () => void;
  onOpenMine: (tab: MineTab) => void;
  onLogin: () => void;
  onRegister: () => void;
  onSignOut: () => void;
}> = ({
  profile,
  connection,
  screen,
  onNavigate,
  friendRequests,
  onCreateQuiz,
  onCreateExam,
  onCreateTask,
  onOpenMine,
  onLogin,
  onRegister,
  onSignOut,
}) => (
  <header className="sticky top-0 z-40 border-b border-ink-800 bg-ink-950/85 backdrop-blur-xl">
    <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-2 sm:gap-3">
      <button onClick={() => onNavigate('home')} aria-label={`Inicio de ${BRAND.name}`} className="shrink-0">
        {/* En el teléfono solo el icono: con el nombre, la barra no cabía y
            el avatar se salía de la pantalla. */}
        <Logo size={34} wordmarkClassName="hidden min-[480px]:flex" />
      </button>

      <nav className="ml-auto flex items-center gap-0 sm:gap-1">
        <NavButton
          active={screen === 'leaderboard'}
          onClick={() => onNavigate('leaderboard')}
          icon={<Trophy className="w-4 h-4" />}
          label="Ranking"
        />
        {/* Ligas vive dentro de Jugadores, como una pestaña más. */}
        <NavButton
          active={screen === 'users' || screen === 'user' || screen === 'leagues'}
          onClick={() => onNavigate('users')}
          icon={<Users className="w-4 h-4" />}
          label="Jugadores"
        />
        {profile && (
          <NavButton
            active={screen === 'friends'}
            onClick={() => onNavigate('friends')}
            icon={<UserPlus className="w-4 h-4" />}
            label="Amigos"
            badge={friendRequests}
          />
        )}
        {/* Perfil, Dahus y el tema viven en el menu del avatar. Aqui se queda
            solo lo que se usa a diario. */}
      </nav>

      <CreateMenu onCreateQuiz={onCreateQuiz} onCreateExam={onCreateExam} onCreateTask={onCreateTask} />

      {profile ? (
        <UserMenu
          profile={profile}
          onProfile={() => onNavigate('profile')}
          onMyQuizzes={() => onOpenMine('quizzes')}
          onMyExams={() => onOpenMine('exams')}
          onMyTasks={() => onOpenMine('tasks')}
          onWallet={() => onNavigate('wallet')}
          onGuardian={() => onNavigate('guardian')}
          onRecordings={() => onNavigate('recordings')}
          onAdmin={() => onNavigate('admin')}
          onSignOut={onSignOut}
        />
      ) : (
        <>
          {/* Sin sesion no hay menu de cuenta donde meter el tema, asi que se
              queda en la barra: si no, quien aun no se ha registrado no podria
              cambiarlo. */}
          <ThemeToggle compact />
          {/* Dos acciones distintas, cada una con su nombre: quien ya tiene
              cuenta no deberia tener que adivinar si el boton le va a pedir
              crear una.

              En movil no caben las dos escritas, asi que se queda la de
              iniciar sesion reducida a su icono; quien aun no tiene cuenta la
              crea desde el propio dialogo, que ofrece el cambio. */}
          <button
            onClick={onLogin}
            aria-label="Iniciar sesión"
            className="sm:hidden flex items-center justify-center w-10 h-10 rounded-lg shrink-0
                       bg-sky-600 hover:bg-sky-500 text-white transition-colors"
          >
            <LogIn className="w-4 h-4" />
          </button>
          <button
            onClick={onLogin}
            className="hidden sm:inline-flex items-center px-3 h-10 rounded-lg font-semibold text-sm
                       whitespace-nowrap shrink-0 text-ink-300 hover:text-ink-50 hover:bg-ink-850
                       transition-colors"
          >
            Iniciar sesión
          </button>
          <button
            onClick={onRegister}
            className="hidden sm:inline-flex items-center px-4 h-10 rounded-lg font-semibold text-sm
                       whitespace-nowrap shrink-0 bg-sky-600 hover:bg-sky-500 text-white transition-colors"
          >
            Registrarse
          </button>
        </>
      )}
    </div>
  </header>
);

/** «Crear» abre un menú: trivia (partida en vivo), examen (cada uno a su ritmo) o tarea (entrega en PDF). */
const CreateMenu: React.FC<{ onCreateQuiz: () => void; onCreateExam: () => void; onCreateTask: () => void }> = ({
  onCreateQuiz,
  onCreateExam,
  onCreateTask,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Crear"
        className="flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg font-semibold text-sm
                   bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-100 transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Crear</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-xl border border-ink-700 bg-ink-900 shadow-xl p-1.5 z-50 animate-pop"
        >
          <button
            role="menuitem"
            onClick={pick(onCreateQuiz)}
            className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-ink-800 transition-colors"
          >
            <Radio className="w-4 h-4 mt-0.5 text-ember-400 shrink-0" />
            <span>
              <span className="block text-sm font-semibold text-ink-50">Crear trivia</span>
              <span className="block text-xs text-ink-400">Partida en vivo con PIN, contra reloj</span>
            </span>
          </button>
          <button
            role="menuitem"
            onClick={pick(onCreateExam)}
            className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-ink-800 transition-colors"
          >
            <FileText className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
            <span>
              <span className="block text-sm font-semibold text-ink-50">Crear examen</span>
              <span className="block text-xs text-ink-400">Cada uno a su ritmo, con tiempo total y nota</span>
            </span>
          </button>
          <button
            role="menuitem"
            onClick={pick(onCreateTask)}
            className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-ink-800 transition-colors"
          >
            <ClipboardList className="w-4 h-4 mt-0.5 text-magenta-400 shrink-0" />
            <span>
              <span className="block text-sm font-semibold text-ink-50">Crear tarea</span>
              <span className="block text-xs text-ink-400">Instrucciones y entrega en PDF, como en un aula</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

const NavButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  /** Contador en rojo (solicitudes de amistad sin contestar). */
  badge?: number;
}> = ({ active, onClick, icon, label, badge }) => (
  <button
    onClick={onClick}
    aria-label={badge ? `${label} (${badge} pendientes)` : undefined}
    className={`relative flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      active ? 'bg-ink-800 text-ink-50' : 'text-ink-400 hover:text-ink-100 hover:bg-ink-850'
    }`}
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
    {badge ? (
      <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-ember-500 text-on-accent text-[10px] font-bold flex items-center justify-center">
        {badge}
      </span>
    ) : null}
  </button>
);

/** Jugadores y Ligas comparten entrada en la barra: se cambia entre ellas aquí. */
const PlayersTabs: React.FC<{
  active: 'users' | 'leagues';
  onChange: (s: 'users' | 'leagues') => void;
}> = ({ active, onChange }) => (
  <div className="max-w-3xl mx-auto px-4 pt-6 -mb-2">
    <div role="tablist" className="flex p-1 rounded-xl bg-ink-850 border border-ink-700">
      {([
        ['users', 'Jugadores', <Users key="u" className="w-4 h-4" />],
        ['leagues', 'Ligas', <Shield key="l" className="w-4 h-4" />],
      ] as const).map(([id, label, icon]) => (
        <button
          key={id}
          role="tab"
          aria-selected={active === id}
          onClick={() => onChange(id)}
          className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-semibold transition-colors ${
            active === id ? 'bg-ink-700 text-ink-50 shadow' : 'text-ink-400 hover:text-ink-100'
          }`}
        >
          {icon}
          {label}
        </button>
      ))}
    </div>
  </div>
);

/* ================================================================
 * Inicio
 * ================================================================ */

const HomeScreen: React.FC<{
  quizzes: QuizSummary[];
  loading: boolean;
  search: string;
  onSearch: (s: string) => void;
  tab: HomeTab;
  onTab: (tab: HomeTab) => void;
  language: string;
  onLanguage: (code: string) => void;
  languagesInUse: { code: string; count: number }[];
  profile: Profile | null;
  onHost: (quiz: QuizSummary) => void;
  onStudy: (quizId: string) => void;
  onJoin: () => void;
  onJoinRoom: (pin: string) => void;
  onCreateMatch: () => void;
  onCreate: () => void;
  favoriteIds: Set<string>;
  onToggleFavorite: (quizId: string) => void;
  onOpenQuiz: (quizId: string) => void;
}> = ({
  quizzes,
  loading,
  search,
  onSearch,
  tab,
  onTab: setTab,
  language,
  onLanguage,
  languagesInUse,
  profile,
  onHost,
  onStudy,
  onJoin,
  onJoinRoom,
  onCreateMatch,
  onCreate,
  favoriteIds,
  onToggleFavorite,
  onOpenQuiz,
}) => {
  const rooms = useLobbyRooms();
  // El catálogo se enseña por tandas: todo de golpe era una pared de tarjetas.
  // Buscar o cambiar de idioma vuelve a la primera tanda.
  const [visibleCount, setVisibleCount] = useState(QUIZ_PAGE_SIZE);
  useEffect(() => setVisibleCount(QUIZ_PAGE_SIZE), [search, language]);

  return (
  <>
    {/* Portada */}
    <Hero profile={profile} onJoin={onJoin} onCreate={onCreate} />

    {/* Catálogo y sala de espera comparten un mismo panel con pestañas: una
        debajo de la otra obligaban a bajar mucho para llegar a los quizzes. */}
    <section className="max-w-7xl mx-auto px-4 pb-16">
      <div className="flex items-end gap-x-3 flex-wrap sm:border-b border-ink-700">
        {/* En móvil los filtros bajan a su propia línea: la raya va con las
            pestañas para que no quede separada de su subrayado. */}
        <div role="tablist" aria-label="Contenido de inicio" className="flex w-full sm:w-auto border-b sm:border-b-0 border-ink-700">
          <HomeTabButton
            active={tab === 'quizzes'}
            onClick={() => setTab('quizzes')}
            icon={<LayoutGrid className="w-4 h-4" />}
            label="Quizzes"
          />
          <HomeTabButton
            active={tab === 'rooms'}
            onClick={() => setTab('rooms')}
            icon={<Radio className="w-4 h-4" />}
            label="Sala de espera"
            count={rooms?.length}
          />
        </div>

        {tab === 'quizzes' && (
        <div className="flex items-center gap-3 flex-wrap ml-auto pt-3 sm:pt-0 sm:pb-2 w-full sm:w-auto">
        {/* Solo se ofrecen los idiomas que tienen trivias detras: un desplegable
            con treinta opciones de las que veintiocho no dan resultados no
            clasifica nada, solo estorba. */}
        {languagesInUse.length > 1 && (
          <select
            value={language}
            onChange={(e) => onLanguage(e.target.value)}
            aria-label="Filtrar por idioma"
            className="ml-auto sm:ml-0 h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-200 text-sm
                       outline-none transition-colors focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
          >
            <option value="">Todos los idiomas</option>
            {languagesInUse.map((l) => (
              <option key={l.code} value={l.code}>
                {languageName(l.code) ?? l.code} ({l.count})
              </option>
            ))}
          </select>
        )}

        <div className="relative flex-1 sm:flex-none sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Buscar por tema…"
            aria-label="Buscar quizzes"
            type="search"
            className="w-full h-11 pl-9 pr-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm
                       placeholder:text-ink-500 outline-none transition-colors
                       focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
          />
        </div>
        </div>
        )}
      </div>

      {/* Alto fijo con desplazamiento interno desde tableta, como el lobby de
          lichess; en móvil la lista fluye con la página. Ver `.home-panel`. */}
      <div role="tabpanel" className="home-panel">
      {tab === 'rooms' ? (
        <div className="home-rooms">
          <OpenRooms rooms={rooms} onJoin={onJoinRoom} onCreateMatch={onCreateMatch} />
        </div>
      ) : (
      <div className="pt-3 sm:p-4">
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="surface h-40 shimmer" />
          ))}
        </div>
      ) : quizzes.length === 0 ? (
        <div className="surface p-12 text-center space-y-3">
          <Sparkles className="w-10 h-10 mx-auto text-ink-600" />
          <p className="text-ink-300">
            {search ? `Nada encontrado para "${search}".` : 'Todavía no hay quizzes publicados.'}
          </p>
          <button onClick={onCreate} className="text-sky-400 hover:text-sky-300 font-medium">
            Crear el primero
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {quizzes.slice(0, visibleCount).map((quiz) => (
            <article key={quiz.id} className="surface p-4 sm:p-5 flex flex-col gap-3 hover:border-ink-600 transition-colors">
              <div className="flex items-start gap-3">
                <span className="font-emoji text-4xl leading-none" aria-hidden="true">
                  {quiz.coverEmoji}
                </span>
                <div className="min-w-0 flex-1">
                  {/* Enlace de verdad a la ficha: abrirlo en otra pestaña o
                      copiar su dirección también sirve para compartirla. */}
                  <h3 className="font-bold text-ink-50 leading-snug line-clamp-2">
                    <a
                      href={`/t/${encodeURIComponent(quiz.id)}`}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        onOpenQuiz(quiz.id);
                      }}
                      className="hover:text-sky-300 transition-colors"
                    >
                      {quiz.title}
                    </a>
                  </h3>
                  <p className="text-ink-500 text-xs mt-1">
                    {quiz.category} · {quiz.questionCount}{' '}
                    {quiz.questionCount === 1 ? 'pregunta' : 'preguntas'}
                    {quiz.languageName && ` · ${quiz.languageName}`}
                  </p>
                </div>
                <FavoriteButton
                  active={favoriteIds.has(quiz.id)}
                  title={quiz.title}
                  onToggle={() => onToggleFavorite(quiz.id)}
                />
              </div>

              {quiz.description && (
                <p className="text-ink-400 text-sm line-clamp-2 flex-1">{quiz.description}</p>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => onHost(quiz)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-semibold text-sm bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
                >
                  <Play className="w-4 h-4" fill="currentColor" />
                  Anfitrión
                </button>
                <button
                  onClick={() => onStudy(quiz.id)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-semibold text-sm bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-200 transition-colors"
                  title="Practicar a tu ritmo, sin sala"
                >
                  <BookOpen className="w-4 h-4" />
                  Estudiar
                </button>
                <ShareButton quiz={quiz} />
              </div>
            </article>
          ))}
        </div>
      )}
      {!loading && quizzes.length > visibleCount && (
        <div className="flex flex-col items-center gap-1.5 pt-5">
          <button
            onClick={() => setVisibleCount((n) => n + QUIZ_PAGE_SIZE)}
            className="px-6 h-11 rounded-lg font-semibold text-sm bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-100 transition-colors"
          >
            Ver más
          </button>
          <p className="text-ink-500 text-xs">
            {visibleCount} de {quizzes.length} · o usa el buscador
          </p>
        </div>
      )}
      </div>
      )}
      </div>
    </section>
  </>
  );
};


/** Trivias por tanda en el catálogo de inicio. */
const QUIZ_PAGE_SIZE = 15;

const HomeTabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}> = ({ active, onClick, icon, label, count }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex items-center gap-2 px-4 sm:px-6 py-3 -mb-px border-b-2 text-sm sm:text-base font-semibold
                transition-colors ${
                  active
                    ? 'border-ember-500 text-ink-50'
                    : 'border-transparent text-ink-400 hover:text-ink-100'
                }`}
  >
    {icon}
    {label}
    {count !== undefined && count > 0 && (
      <span className="min-w-5 h-5 px-1.5 rounded-full bg-ember-500 text-on-accent text-xs font-bold
                       flex items-center justify-center tabular-nums">
        {count}
      </span>
    )}
  </button>
);

const Footer: React.FC<{ onLegal: (doc: LegalDoc) => void }> = ({ onLegal }) => (
  <footer className="border-t border-ink-800 py-6">
    {/* Sin eslóganes ni avisos: lo de que los Dahus no valen dinero ya lo
        explica la cartera, que es donde importa. */}
    <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-ink-500">
      <span className="flex items-center gap-2">
        <AppIcon size={20} />
        © {new Date().getFullYear()} {BRAND.name}
      </span>
      {/* Enlaces de verdad, para que se puedan abrir aparte o copiar. */}
      <a
        href="/terminos"
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onLegal('terminos');
        }}
        className="hover:text-ink-200"
      >
        Términos
      </a>
      <a
        href="/privacidad"
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onLegal('privacidad');
        }}
        className="hover:text-ink-200"
      >
        Privacidad
      </a>
    </div>
  </footer>
);
